import React, { useCallback, useContext } from "react";

import cx from "classnames";

import AppContext from "~/common/AppContext";
import { autoBadge } from "~/stream/UserBadges";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { Modal } from "~/ui/legacyFellowIndex";

import type { teamQuery_Info$data as Team } from "~/__generated__/teamQuery_Info.graphql";

import stylesSharedWithModal from "~/stream/css/SharedWithModal.module.css";

import styles from "./TeamMembersModal.module.css";

interface TeamMembersModalProps {
	onClose(): void;
	onClickEditTeamMembers(): void;
	teamTitle: Team["title"];
	members: Team["members"];
	sync: Team["sync"];
	hasEditAccess?: boolean;
	isGoogleWorkspace: boolean;
}

export default function TeamMembersModal({
	onClose,
	onClickEditTeamMembers,
	teamTitle,
	members,
	sync,
	hasEditAccess,
	isGoogleWorkspace,
}: TeamMembersModalProps) {
	const { user } = useContext(AppContext);

	const onEditTeamMembers = useCallback(() => {
		onClose();
		onClickEditTeamMembers();
	}, [onClose, onClickEditTeamMembers]);

	return (
		<Modal onClose={onClose} width="21rem">
			<Modal.Header onClose={onClose}>
				<Modal.Title>Team members</Modal.Title>
			</Modal.Header>
			<Modal.Body className={stylesSharedWithModal.body}>
				{sync?.fromManager ? (
					<h6>
						<b>{teamTitle}</b> team members are synced with {sync?.fromManager.fullName} and their direct reports.
					</h6>
				) : (
					sync?.fromExternalGroupName && (
						<h6>
							<b>{teamTitle}</b> team members are synced with {sync?.fromExternalGroupName}{" "}
							{isGoogleWorkspace ? "Google Workspace" : "Microsoft 365"} group
						</h6>
					)
				)}
				{members.length === 0 && <p className={styles.noTeamMembers}>No team members</p>}
				{members.map(member => (
					<div
						key={member.id}
						className={cx(styles.grid, stylesSharedWithModal.grid, stylesSharedWithModal.userContainer)}
					>
						<Avatar user={member} link={false} width="2em" className={stylesSharedWithModal.userAvatar} />
						<h4 className={stylesSharedWithModal.userName}>
							{member.fullName}
							{member.id === user?.id ? " (You)" : autoBadge(member)}
						</h4>
						<p className={stylesSharedWithModal.userEmail}>{member.email}</p>
					</div>
				))}
			</Modal.Body>
			<Modal.Footer>
				{hasEditAccess && (
					<Button size="normal" variant="secondary" onClick={onEditTeamMembers}>
						Edit team members
					</Button>
				)}
				<Button size="normal" variant="primary" onClick={onClose}>
					Done
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
